import { Controller } from '@hotwired/stimulus'
import { useApplication, useIntersection } from 'stimulus-use'
import Cookies from 'js-cookie'
export default class extends Controller {
  static targets = ['button']
  static values = { url: String, markUrl: String, unmarkUrl: String, joinUrl: String }

  connect () {
    useApplication(this)
    useIntersection(this)
    this.element['activityButtonsController'] = this

    this.boundMarkedListener = this.handleMarked.bind(this)
    this.boundUnmarkedListener = this.handleUnmarked.bind(this)

    this.element.addEventListener(
      'activity-streams--button:marked',
      this.boundMarkedListener
    )
    this.element.addEventListener(
      'activity-streams--button:unmarked',
      this.boundUnmarkedListener
    )
  }

  disconnect () {
    this.element.removeEventListener(
      'activity-streams--button:marked',
      this.boundMarkedListener
    )
    this.element.removeEventListener(
      'activity-streams--button:unmarked',
      this.boundUnmarkedListener
    )
  }

  appear () {
    const userSignedIn = Cookies.get('user_signed_in') === 'true'

    if (!this.markedOnAppearance && userSignedIn) {
      this.replayPendingActivityStream()
      this.markButtons()
      this.markedOnAppearance = true
    }
  }

  replayPendingActivityStream () {
    let raw
    try {
      raw = localStorage.getItem('pendingActivityStream')
    } catch (_) {
      return
    }
    if (!raw) return

    // Claim the pending action atomically via a transient marker key so only
    // one of N controllers on the page proceeds to replay.
    const claim = 'pendingActivityStream.claim'
    try {
      const existingClaim = localStorage.getItem(claim)
      if (existingClaim && Number(existingClaim) > Date.now() - 5000) {
        return
      }
      localStorage.setItem(claim, String(Date.now()))
    } catch (_) {}

    let pending
    try {
      pending = JSON.parse(raw)
    } catch (_) {
      try { localStorage.removeItem('pendingActivityStream') } catch (_) {}
      try { localStorage.removeItem(claim) } catch (_) {}
      return
    }

    if (!pending || !pending.sgid || !pending.kind) {
      try { localStorage.removeItem('pendingActivityStream') } catch (_) {}
      try { localStorage.removeItem(claim) } catch (_) {}
      return
    }

    if (!pending.storedAt || Date.now() - pending.storedAt > 24 * 60 * 60 * 1000) {
      try { localStorage.removeItem('pendingActivityStream') } catch (_) {}
      try { localStorage.removeItem(claim) } catch (_) {}
      return
    }

    try {
      if (pending.returnUrl) {
        const storedPath = new URL(pending.returnUrl).pathname
        const currentPath = window.location.pathname
        if (storedPath !== currentPath) {
          try { localStorage.removeItem(claim) } catch (_) {}
          return
        }
      }
    } catch (_) {
      // malformed stored URL — treat as stale
      try { localStorage.removeItem('pendingActivityStream') } catch (_) {}
      try { localStorage.removeItem(claim) } catch (_) {}
      return
    }

    const button = this.buttonTargets.find(el => {
      const ctrl = el.activityStreamButton
      if (!ctrl) return false
      return ctrl.resourceSgidValue === pending.sgid && ctrl.kindValue === pending.kind
    })
    if (!button) {
      try { localStorage.removeItem(claim) } catch (_) {}
      return
    }

    const action = pending.action === 'unmark' ? 'unmark' : 'mark'
    const url = action === 'unmark' ? this.unmarkUrlValue : this.markUrlValue
    const ctrl = button.activityStreamButton

    ctrl.disabled = true
    const posthogSessionId = this.posthogSessionId()

    const abortController = new AbortController()
    const abortTimeout = setTimeout(() => abortController.abort(), 12000)

    fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken,
        'X-PostHog-Session-Id': posthogSessionId
      },
      body: JSON.stringify({
        sgid: pending.sgid,
        kind: pending.kind,
        posthog_session_id: posthogSessionId
      }),
      signal: abortController.signal
    })
      .then(response => response.json())
      .then(data => {
        if (data && data.message === 'ok') {
          try { localStorage.removeItem('pendingActivityStream') } catch (_) {}
          ctrl.marked = action === 'mark'
          const eventName = 'activity-streams--button:' + (action === 'mark' ? 'marked' : 'unmarked')
          if (button.isConnected) {
            button.dispatchEvent(new CustomEvent(eventName, {
              detail: { kind: pending.kind, resourceSgid: pending.sgid, source: button, replayed: true },
              bubbles: true,
              cancelable: true
            }))
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        clearTimeout(abortTimeout)
        try { localStorage.removeItem(claim) } catch (_) {}
        if (this.element.isConnected) ctrl.disabled = false
      })
  }

  markButtons () {
    let resourceIds = []
    let resourceTypes = []

    this.buttonTargets.forEach(button => {
      resourceIds.push(button.activityStreamButton.resourceIdValue)
      resourceTypes.push(button.activityStreamButton.resourceTypeValue)
    })

    resourceIds = [...new Set(resourceIds)].join(',')
    resourceTypes = [...new Set(resourceTypes)].join(',')

    const urlParams = new URLSearchParams({
      ids: resourceIds,
      streamable_types: resourceTypes
    })

    const url = `${this.urlValue}?${urlParams.toString()}`
    fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => response.json())
      .then(buttonMarks => {
        this.buttonTargets.forEach(button => {
          let markArr = buttonMarks[button.activityStreamButton.markIdValue]
          let kind = button.activityStreamButton.kindValue.replace(/-/g, '_')
          if (markArr && markArr.includes(kind)) {
            button.activityStreamButton.marked = true
          }
        })
      })
      .catch(error => {
        // NOOP: do nothing, since we don't know if we can mark anything
      })
  }

  handleMarked (event) {
    if (event.detail && event.detail.replayed) return
    const userSignedIn = Cookies.get('user_signed_in') === 'true'
    if (userSignedIn) {
      this._toggleMarked(event, this.markUrlValue)
    } else {
      this._redirectToJoinWithPendingAction(event, 'mark')
    }
  }

  handleUnmarked (event) {
    if (event.detail && event.detail.replayed) return
    const userSignedIn = Cookies.get('user_signed_in') === 'true'
    if (userSignedIn) {
      this._toggleMarked(event, this.unmarkUrlValue)
    } else {
      this._redirectToJoinWithPendingAction(event, 'unmark')
    }
  }

  _redirectToJoinWithPendingAction (event, action) {
    try {
      localStorage.setItem('pendingActivityStream', JSON.stringify({
        sgid: event.detail.resourceSgid,
        kind: event.detail.kind,
        action: action,
        returnUrl: window.location.href,
        storedAt: Date.now()
      }))
    } catch (_) {}

    const cause = { 'has-done': 'been', 'wants-to': 'want' }[event.detail.kind]
    const intent = encodeURIComponent(window.location.href)
    const sep = this.joinUrlValue.includes('?') ? '&' : '?'
    const smallScreenSuffix = window.innerWidth < 668 ? '&small_screen=1' : ''
    window.location.href = `${this.joinUrlValue}${sep}cause=${cause}&skip_profile=1&intent=${intent}${smallScreenSuffix}`
  }

  _toggleMarked (event, url) {
    event.detail.source.activityStreamButton.disabled = true
    const posthogSessionId = this.posthogSessionId()

    fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken,
        'X-PostHog-Session-Id': posthogSessionId
      },
      body: JSON.stringify({
        sgid: event.detail.resourceSgid,
        kind: event.detail.kind,
        posthog_session_id: posthogSessionId
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data['message'] && data['message'] === 'ok') {
          // it's optimistically marked, so we don't need to do much
          event.detail.source.activityStreamButton.disabled = false
        }
      })
      .catch(error => {
        event.detail.source.activityStreamButton.disabled = false
        event.detail.source.classList.remove('disabled')
      })
  }

  posthogSessionId () {
    if (typeof window.posthog === 'undefined') return null
    if (typeof window.posthog.get_session_id !== 'function') return null

    return window.posthog.get_session_id()
  }
}
